// Orchestrator voor de signup-flow. Zie docs/features/onboarding-flow.md (T10).
//
// Verantwoordelijk voor:
//   - URL-driven step (`/signup/:step?`)
//   - Step-sequence via getStepSequence (gebruikt OnboardingFlow.modules)
//   - Progress-bar (current index / total length)
//   - Resume-on-return: ingelogde gebruiker met `onboardingStatus.completed === false`
//     en `lastStep` wordt bij `/signup` (zonder step in URL) doorgestuurd naar
//     `/signup/<lastStep>`.
//
// Step-componenten zijn nu nog stubs (T9); worden in Phase C-E vervangen.

import { useContext, useEffect, useMemo, useRef } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import { OnboardingFlow as OnboardingFlowContext } from '../../StateManagment/OnboardingFlow'
import { Auth } from '../../StateManagment/Auth'
import useSettings from '../../hooks/Settings'
import getStepSequence from '../../helpers/onboardingHelpers/getStepSequence'
import useDevPreview from '../../hooks/useDevPreview'
import parseDeeplink from '../../helpers/onboardingHelpers/parseDeeplink'
import { PARTNER_PACKAGES } from '../../helpers/subscriptionHelpers/moduleDefinitions'
import applyPartnerContext from '../../helpers/onboardingHelpers/applyPartnerContext'
import usePartner from '../../hooks/usePartner'
import useCompagnyMetaById from '../../hooks/useCompagnyMetaById'
import deccosLogo from '../../images/deccos-logo.png'

// Bewust geen LayoutOpen — de signup-flow heeft een eigen, schone canvas
// zonder Topbar (en dus ook zonder Decca-icon).

import Step1Account from './steps/Step1Account'
import Step2OrgInfo from './steps/Step2OrgInfo'
import Step3MeasurementApproach from './steps/Step3MeasurementApproach'
import Step4Modules from './steps/Step4Modules'
import Step5Pricing from './steps/Step5Pricing'
import Step7Success from './steps/Step7Success'
import Step9AITOC from './steps/Step9AITOC'
import Step10MeasurementPlan from './steps/Step10MeasurementPlan'
import Step11Activation from './steps/Step11Activation'

const STEP_COMPONENTS = {
    '1': Step1Account,
    '2': Step2OrgInfo,
    '3': Step3MeasurementApproach,
    '4': Step4Modules,
    '5': Step5Pricing,
    '7': Step7Success,
    '9': Step9AITOC,
    '10': Step10MeasurementPlan,
    '11': Step11Activation,
}

const OnboardingFlow = () => {

    // Hooks
    const params = useParams()
    const history = useHistory()
    const location = useLocation()
    const [authO] = useContext(Auth)
    const {
        modules,
        setModules,
        lockedModules,
        setLockedModules,
        setPartnerSlug,
        setPartner,
        setPartnerLoading,
    } = useContext(OnboardingFlowContext)
    const { onboardingStatus } = useSettings()
    const { active: devPreview, disable: disableDevPreview } = useDevPreview()

    // Deeplink + partner-tenant drijven het logo (white-label-MVP).
    // Resolutie: Partners/{...}.logo wint (rechtstreeks veld op het partner-
    // record). Als die ontbreekt, val terug op de CompagnyMeta.Logo van de
    // partner-tenant — daarna pas op het default Deccos-logo.
    //
    // We doen hier de ENIGE `usePartner`-subscribe voor de hele flow. Resultaat
    // schrijven we naar context (`partner`, `partnerLoading`) zodat stappen
    // geen eigen Firestore-listener hoeven op te zetten.
    const deeplink = useMemo(() => parseDeeplink(location.search), [location.search])
    const { partner, loading: partnerLoading } = usePartner(deeplink.partnerSlug)
    const { compagnyMeta: partnerTenant } = useCompagnyMetaById(partner?.networkCompagnyID)
    const headerLogo = partner?.logo || partnerTenant?.Logo || deccosLogo
    const headerLogoAlt = partner?.name
        ? `${partner.name} logo`
        : (partnerTenant?.CommunityName ? `${partnerTenant.CommunityName} logo` : 'Deccos logo')

    // Merge Firestore partner-record met local PARTNER_PACKAGES config zodat
    // downstream stappen (Step 5, etc.) altijd de canonieke prijs + combo-
    // tier-config zien, ook als Firestore stale `fixedPackage.price` waarden
    // bevat van eerdere seeds. Firestore wint voor `modules`/`freeIncludedModules`/
    // branding; PARTNER_PACKAGES wint voor `price`/`stripePriceId`/`packages`.
    const mergedPartner = useMemo(() => {
        if (!partner) return null
        const localConfig = partner.slug ? PARTNER_PACKAGES[partner.slug] : null
        if (!localConfig) return partner
        return {
            ...partner,
            fixedPackage: partner.fixedPackage
                ? { ...partner.fixedPackage, ...localConfig.fixedPackage }
                : localConfig.fixedPackage || null,
            packages: localConfig.packages || partner.packages || null,
        }
    }, [partner])

    useEffect(() => {
        setPartner(mergedPartner)
        setPartnerLoading(partnerLoading)
    }, [mergedPartner, partnerLoading, setPartner, setPartnerLoading])

    // T26 — one-time seed van modules + lockedModules op basis van deeplink+partner.
    // Wacht tot partner geladen is als er een partnerSlug in URL staat.
    // Daarna mag Step 4 (en andere stappen) modules zelf updaten.
    const seededRef = useRef(false)

    useEffect(() => {

        if (seededRef.current) return

        // Wacht op partner-fetch als er een partnerSlug in URL staat
        if (deeplink.partnerSlug && partnerLoading) return

        const ctx = applyPartnerContext({ partner, deeplink })
        setModules(ctx.initialModules)
        setLockedModules(ctx.lockedModules)
        setPartnerSlug(deeplink.partnerSlug || null)
        seededRef.current = true

    }, [deeplink, partner, partnerLoading, setModules, setLockedModules, setPartnerSlug])

    // Sequence — drives skipping voor module-only en partner-flows.
    // Bij default-shape (lege `modules`, geen partner): [1, 2, 4, 5, 6, 7].
    // Met partner: stap 3 wordt geskipt (partner-TOC overrijdt sector-keuze).
    const sequence = useMemo(
        () => getStepSequence({
            activeModules: modules,
            partnerSlug: deeplink.partnerSlug,
        }),
        [modules, deeplink.partnerSlug]
    )

    // Resume-on-return: alleen als gebruiker is ingelogd, een onafgemaakte
    // onboardingStatus heeft, en geen step in URL meegeeft. In dev-preview
    // skippen we deze logica zodat je telkens schoon op stap 1 begint.
    useEffect(() => {

        if (devPreview) return
        if (params.step) return
        if (!authO) return
        if (!onboardingStatus) return
        if (onboardingStatus.completed) return
        if (!onboardingStatus.lastStep) return

        history.replace(`/signup/${onboardingStatus.lastStep}`)

    }, [params.step, authO, onboardingStatus, history, devPreview])

    // Effective step — fallback naar eerste in sequence als URL-param leeg of onbekend is.
    const fallbackStepId = String(sequence[0])
    const requestedStepId = params.step ? String(params.step) : fallbackStepId
    const stepIndex = sequence.findIndex(id => String(id) === requestedStepId)
    const safeIndex = stepIndex < 0 ? 0 : stepIndex
    const currentStepId = String(sequence[safeIndex])
    const StepComponent = STEP_COMPONENTS[currentStepId]

    // Navigation handlers — search/query string (?partner=…, ?network=…, ?dev=1)
    // moet meereizen, anders verliest de flow zijn deeplink-context na Stap 1
    // en valt de partner-branding terug op het Deccos-default-logo.
    const goToIndex = (idx) => {
        if (idx < 0 || idx >= sequence.length) return
        history.push({
            pathname: `/signup/${sequence[idx]}`,
            search: location.search,
        })
    }

    const onNext = () => goToIndex(safeIndex + 1)
    const onPrev = () => goToIndex(safeIndex - 1)

    // Progress-bar telt alleen genummerde stappen.
    const numberedSteps = sequence.filter((id) => typeof id === 'number')
    const totalSteps = numberedSteps.length
    const isNumberedStep = typeof sequence[safeIndex] === 'number'
    const numberedIndex = isNumberedStep
        ? numberedSteps.findIndex((id) => id === sequence[safeIndex])
        : -1
    const progressPercentage = isNumberedStep
        ? Math.round(((numberedIndex + 1) / totalSteps) * 100)
        : 100

    const handleDisableDevPreview = () => {
        disableDevPreview()
        // Reset URL — verwijder ?dev params + ga naar /signup zodat de
        // resume-detectie weer kan firen voor echte sessies.
        history.replace('/signup')
    }

    return (
        <div className="onboarding-flow-page">
            {devPreview && (
                <div className="onboarding-dev-banner" role="status">
                    <span className="onboarding-dev-banner-label">Dev preview</span>
                    <span className="onboarding-dev-banner-text">
                        Geen echte writes naar Firebase. Form-data verdwijnt bij refresh.
                    </span>
                    <button
                        type="button"
                        className="onboarding-dev-banner-button"
                        onClick={handleDisableDevPreview}
                    >
                        Verlaat dev preview
                    </button>
                </div>
            )}

            <div className="onboarding-flow-header">
                <img
                    src={headerLogo}
                    alt={headerLogoAlt}
                    className="onboarding-flow-logo"
                />
            </div>

            <div className="onboarding-flow-container">
                {isNumberedStep && (
                    <div className="onboarding-progress">
                        <div className="onboarding-progress-track">
                            <div
                                className="onboarding-progress-fill"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>
                        <p className="onboarding-progress-label">
                            Stap {numberedIndex + 1} van {totalSteps}
                        </p>
                    </div>
                )}

                {StepComponent && (
                    <StepComponent
                        onNext={onNext}
                        onPrev={onPrev}
                        isFirst={safeIndex === 0}
                        isLast={safeIndex === sequence.length - 1}
                    />
                )}
            </div>
        </div>
    )

}

export default OnboardingFlow
